// src/components/Layout/AchievementsPanel.js
import React, { useState, useMemo } from 'react';
import styles from './AchievementsPanel.module.css';
import { achievementData } from '../../data/data';

const AchievementsPanel = ({ playerData, playerStats = {} }) => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showCompleted, setShowCompleted] = useState(false);
  const [showIncomplete, setShowIncomplete] = useState(true);
  const [showLocked, setShowLocked] = useState(false);
  const [sortBy, setSortBy] = useState('category'); // 'category', 'progress', 'reward'

  // Get all unique categories
  const categories = useMemo(() => {
    const cats = ['All', ...new Set(Object.values(achievementData).map(ach => ach.category))];
    return cats;
  }, []);

  // Calculate achievement progress and completion status
  const processedAchievements = useMemo(() => {
    return Object.values(achievementData).map(achievement => {
      const currentValue = playerStats[achievement.targetStat] || 0;
      const progress = Math.min(currentValue / achievement.targetValue, 1);
      const isCompleted = currentValue >= achievement.targetValue;
      const isUnlocked = !achievement.prerequisiteAchId || 
        (playerData.completedAchievements && playerData.completedAchievements.includes(achievement.prerequisiteAchId));

      return {
        ...achievement,
        currentValue,
        progress,
        isCompleted,
        isUnlocked
      };
    });
  }, [playerStats]);

  // Filter achievements based on category and completion status
  const filteredAchievements = useMemo(() => {
    let filtered = processedAchievements;

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(ach => ach.category === selectedCategory);
    }

    // Filter by completion and unlock status
    filtered = filtered.filter(ach => {
      // Hide completed achievements if showCompleted is false
      if (ach.isCompleted && !showCompleted) return false;
      
      // Hide in-progress (unlocked but not completed) achievements if showIncomplete is false
      if (!ach.isCompleted && ach.isUnlocked && !showIncomplete) return false;
      
      // Hide locked achievements if showLocked is false
      if (!ach.isUnlocked && !showLocked) return false;
      
      return true;
    });

    // Sort achievements
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'progress':
          if (a.isCompleted !== b.isCompleted) {
            return a.isCompleted ? 1 : -1; // Incomplete first
          }
          return b.progress - a.progress;
        case 'reward':
          return (b.goldReward || 0) - (a.goldReward || 0);
        case 'category':
        default:
          if (a.category !== b.category) {
            return a.category.localeCompare(b.category);
          }
          if (a.seriesName && b.seriesName && a.seriesName === b.seriesName) {
            return (a.tier || 0) - (b.tier || 0);
          }
          return a.title.localeCompare(b.title);
      }
    });

    return filtered;
  }, [processedAchievements, selectedCategory, showCompleted, showIncomplete, showLocked, sortBy]);

  // Calculate statistics
  const stats = useMemo(() => {
    const completed = processedAchievements.filter(ach => ach.isCompleted).length;
    const total = processedAchievements.length;
    const totalGoldEarned = processedAchievements
      .filter(ach => ach.isCompleted)
      .reduce((sum, ach) => sum + (ach.goldReward || 0), 0);

    const categoryStats = categories.slice(1).map(category => {
      const categoryAchs = processedAchievements.filter(ach => ach.category === category);
      const categoryCompleted = categoryAchs.filter(ach => ach.isCompleted).length;
      return {
        category,
        completed: categoryCompleted,
        total: categoryAchs.length,
        percentage: categoryAchs.length > 0 ? Math.round((categoryCompleted / categoryAchs.length) * 100) : 0
      };
    });

    return {
      completed,
      total,
      percentage: Math.round((completed / total) * 100),
      totalGoldEarned,
      categoryStats
    };
  }, [processedAchievements, categories]);

  const getProgressBarColor = (progress, isCompleted) => {
    if (isCompleted) return '#27ae60';
    if (progress > 0.7) return '#f39c12';
    if (progress > 0.3) return '#e67e22';
    return '#e74c3c';
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 1: return '#cd7f32'; // Bronze
      case 2: return '#c0c0c0'; // Silver
      case 3: return '#ffd700'; // Gold
      case 4: return '#b19cd9'; // Platinum
      default: return '#95a5a6'; // Gray
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  return (
    <div className={styles.achievementsContainer}>
      {/* Header with overall stats */}
      <div className={styles.header}>
        <div className={styles.title}>
          <h2>🏆 Achievements</h2>
          <div className={styles.overallProgress}>
            <span className={styles.progressText}>
              {stats.completed}/{stats.total} ({stats.percentage}%)
            </span>
            <div className={styles.progressBar}>
              <div 
                className={styles.progressFill}
                style={{ 
                  width: `${stats.percentage}%`,
                  backgroundColor: getProgressBarColor(stats.percentage / 100, false)
                }}
              />
            </div>
          </div>
        </div>
        <div className={styles.goldEarned}>
          💰 {formatNumber(stats.totalGoldEarned)}G earned
        </div>
      </div>

      {/* Category progress overview */}
      <div className={styles.categoryOverview}>
        {stats.categoryStats.map(cat => (
          <div 
            key={cat.category} 
            className={`${styles.categoryCard} ${selectedCategory === cat.category ? styles.selected : ''}`}
            onClick={() => setSelectedCategory(cat.category)}
          >
            <div className={styles.categoryName}>{cat.category}</div>
            <div className={styles.categoryProgress}>
              {cat.completed}/{cat.total}
            </div>
            <div className={styles.categoryPercentage}>
              {cat.percentage}%
            </div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className={styles.controls}>
        <div className={styles.categoryTabs}>
          {categories.map(category => (
            <button
              key={category}
              className={`${styles.categoryTab} ${selectedCategory === category ? styles.active : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        <div className={styles.filters}>
          <div className={styles.toggleGroup}>
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
              />
              <span>Completed</span>
            </label>
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={showIncomplete}
                onChange={(e) => setShowIncomplete(e.target.checked)}
              />
              <span>In Progress</span>
            </label>
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={showLocked}
                onChange={(e) => setShowLocked(e.target.checked)}
              />
              <span>Locked</span>
            </label>
          </div>

          <select 
            className={styles.sortSelect}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="category">Sort by Category</option>
            <option value="progress">Sort by Progress</option>
            <option value="reward">Sort by Reward</option>
          </select>
        </div>
      </div>

      {/* Achievements list */}
      <div className={styles.achievementsList}>
        {filteredAchievements.map(achievement => (
          <div 
            key={achievement.id} 
            className={`${styles.achievementCard} ${achievement.isCompleted ? styles.completed : ''} ${!achievement.isUnlocked ? styles.locked : ''}`}
          >
            <div className={styles.achievementHeader}>
              <div className={styles.achievementTitle}>
                <div className={styles.titleRow}>
                  <h3>{achievement.title}</h3>
                  {achievement.tier && (
                    <div 
                      className={styles.tierBadge}
                      style={{ backgroundColor: getTierBadgeColor(achievement.tier) }}
                    >
                      {achievement.tier}
                    </div>
                  )}
                  {achievement.isCompleted && (
                    <div className={styles.completedBadge}>✓</div>
                  )}
                  {!achievement.isUnlocked && (
                    <div className={styles.lockedBadge}>🔒</div>
                  )}
                </div>
                <div className={styles.category}>{achievement.category}</div>
              </div>
              
              {achievement.goldReward > 0 && (
                <div className={styles.reward}>
                  💰 {formatNumber(achievement.goldReward)}G
                </div>
              )}
            </div>

            <div className={styles.achievementContent}>
              <p className={styles.description}>{achievement.description}</p>
              
              {achievement.isUnlocked && (
                <div className={styles.progressSection}>
                  <div className={styles.progressInfo}>
                    <span className={styles.progressNumbers}>
                      {formatNumber(achievement.currentValue)} / {formatNumber(achievement.targetValue)}
                    </span>
                    <span className={styles.progressPercentage}>
                      {Math.round(achievement.progress * 100)}%
                    </span>
                  </div>
                  <div className={styles.progressBar}>
                    <div 
                      className={styles.progressFill}
                      style={{ 
                        width: `${achievement.progress * 100}%`,
                        backgroundColor: getProgressBarColor(achievement.progress, achievement.isCompleted)
                      }}
                    />
                  </div>
                </div>
              )}

              {!achievement.isUnlocked && achievement.prerequisiteAchId && (
                <div className={styles.prerequisite}>
                  <span>🔒 Requires: {achievementData[achievement.prerequisiteAchId]?.title}</span>
                </div>
              )}

              {achievement.seriesName && (
                <div className={styles.seriesInfo}>
                  <span>📚 Series: {achievement.seriesName}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredAchievements.length === 0 && (
        <div className={styles.emptyState}>
          <p>No achievements match your current filters.</p>
        </div>
      )}
    </div>
  );
};

export default AchievementsPanel; 